// Dialog "Melhorar com IA" (§4.5): o usuário descreve em linguagem
// natural o que quer melhorar (ex.: "deixe mais formal, adicione um
// CTA"), gera uma sugestão e vê o resumo + lista de mudanças + preview
// da versão melhorada. "Aplicar" joga o improvedSource de volta no
// editor (onApply → setDraftSource); "Descartar" limpa a sugestão.
//
// Onda 2 (front-first): a sugestão vem de um MOCK de contrato do
// gateway (template-ai-improve.json). A IA real vem depois no backend
// — aqui a UX inteira já está construída contra o contrato ideal.
import { Loader2Icon, SparklesIcon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import type {
  NotificationSearchChannelType,
  NotificationTemplateVariableType,
} from "@/types/notification-center"

import { useImproveTemplateWithAi } from "../template-editor-mutations"
import { TemplatePreview } from "./template-preview"

const aiPrefix =
  "pages.notificationCenter.templateEditorPage.ai"

type TemplateAiDialogProps = {
  templateUuid: string
  source: string
  channel: NotificationSearchChannelType
  variables: NotificationTemplateVariableType[]
  onApply: (improvedSource: string) => void
}

export const TemplateAiDialog = ({
  templateUuid,
  source,
  channel,
  variables,
  onApply,
}: TemplateAiDialogProps) => {
  const { t } = useTranslation()
  const improveMutation =
    useImproveTemplateWithAi(templateUuid)
  const [open, setOpen] = useState(false)
  const [instruction, setInstruction] = useState("")

  const suggestion = improveMutation.data

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      improveMutation.reset()
      setInstruction("")
    }
    setOpen(nextOpen)
  }

  const handleGenerate = () => {
    improveMutation.mutate({
      source,
      instruction: instruction.trim() || undefined,
    })
  }

  const handleApply = () => {
    if (suggestion) {
      onApply(suggestion.improvedSource)
    }
    handleOpenChange(false)
  }

  const handleDiscard = () => {
    improveMutation.reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <SparklesIcon
            className="mr-1 size-4"
            aria-hidden="true"
          />
          {t(`${aiPrefix}.button`)}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(`${aiPrefix}.dialogTitle`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${aiPrefix}.dialogDescription`)}
          </DialogDescription>
        </DialogHeader>

        {suggestion ? (
          <div
            className="space-y-4"
            data-testid="ai-suggestion"
          >
            {/* Resumo do que a IA mudou. */}
            <div className="rounded-md border border-violet-200 bg-violet-50 p-3">
              <h3 className="mb-1 text-sm font-semibold text-violet-800">
                {t(`${aiPrefix}.summaryTitle`)}
              </h3>
              <p className="text-sm text-violet-700">
                {suggestion.summary}
              </p>
            </div>

            {/* Lista de mudanças (bullets). */}
            {suggestion.changes.length > 0 && (
              <div>
                <h3 className="mb-1 text-sm font-semibold">
                  {t(`${aiPrefix}.changesTitle`)}
                </h3>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {suggestion.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Preview da versão melhorada (sanitizado, mesmo
                componente do editor). */}
            <div>
              <h3 className="mb-1 text-sm font-semibold">
                {t(`${aiPrefix}.previewTitle`)}
              </h3>
              <TemplatePreview
                channel={channel}
                source={suggestion.improvedSource}
                variables={variables}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleDiscard}
              >
                {t(`${aiPrefix}.discard`)}
              </Button>
              <Button
                type="button"
                className="btn-whitelabel"
                onClick={handleApply}
              >
                {t(`${aiPrefix}.apply`)}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ai-instruction">
                {t(`${aiPrefix}.instructionLabel`)}
              </Label>
              <Textarea
                id="ai-instruction"
                value={instruction}
                onChange={(event) =>
                  setInstruction(event.target.value)
                }
                placeholder={t(
                  `${aiPrefix}.instructionPlaceholder`
                )}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                className="btn-whitelabel"
                disabled={improveMutation.isPending}
                onClick={handleGenerate}
              >
                {improveMutation.isPending ? (
                  <>
                    <Loader2Icon
                      className="mr-1 size-4 animate-spin"
                      aria-hidden="true"
                    />
                    {t(`${aiPrefix}.generating`)}
                  </>
                ) : (
                  <>
                    <SparklesIcon
                      className="mr-1 size-4"
                      aria-hidden="true"
                    />
                    {t(`${aiPrefix}.generate`)}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
